import { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Check, Minus } from "lucide-react";
import "./RevenueDesign.css";
import { BuyerJourney } from "./BuyerJourney";

const buyerSignals = [
  { label: "New market", value: "Europe launch announced" },
  { label: "New product", value: "Data platform launched" },
  { label: "Hiring", value: "Two AEs start Monday" },
];

const gtmSlices = [
  { name: "Positioning & copy", option: "A reason to choose you", detail: "We make your value clear in the words buyers see, from the first email to the proposal." },
  { name: "Outbound", option: "Qualified meetings booked", detail: "We find decision makers and reach them by email, phone and LinkedIn to book sales calls." },
  { name: "LinkedIn content", option: "Trust before the first call", detail: "We write posts that answer buyers' questions and start conversations with people who respond." },
  { name: "Newsletters", option: "Stay in mind until they buy", detail: "We keep prospects interested between calls with useful advice and customer results." },
  { name: "Gifting & mail", option: "Get past the crowded inbox", detail: "We use relevant gifts and personal mail to reach buyers who ignore digital outreach." },
  { name: "Closing", option: "From meeting to payment", detail: "Our salespeople handle follow-up, objections, proposals and negotiation through to payment." },
  { name: "Sales management", option: "Reps managed for you", detail: "We train the reps, review their calls and keep deals moving. You see progress without chasing updates." },
  { name: "CRM & buyer data", option: "Every buyer's history in one place", detail: "We keep contacts, conversations and next steps in your CRM so every rep knows where to pick up." },
];

const stitchProblems = [
  {
    title: "Lost deals",
    body: "A buyer replies. Nobody follows up. You paid for a lead that never reaches a salesperson.",
  },
  {
    title: "Your time",
    body: "You spend the week chasing reps, agencies and CRM updates instead of winning customers.",
  },
  {
    title: "Wasted budget",
    body: "Reports count emails and meetings, but never show which campaigns bring in paying customers.",
  },
];

// Animate decoration once; the content is readable before the observer runs.
const ScrollAccent = ({ as: Tag = "div", className, children }) => {
  const ref = useRef(null);
  const entered = useInView(ref, { once: true, amount: 0.35, margin: "0px 0px -24px 0px" });
  return <Tag ref={ref} className={className} data-entered={entered}>{children}</Tag>;
};

const polar = (r, angle) => [120 + r * Math.cos(angle), 120 + r * Math.sin(angle)];
const donutPath = (start, end) => {
  const [x0, y0] = polar(116, start);
  const [x1, y1] = polar(116, end);
  const [x2, y2] = polar(84, end);
  const [x3, y3] = polar(84, start);
  return `M ${x0} ${y0} A 116 116 0 0 1 ${x1} ${y1} L ${x2} ${y2} A 84 84 0 0 0 ${x3} ${y3} Z`;
};

const GtmPie = () => {
  const [active, setActive] = useState(0);
  const [separated, setSeparated] = useState(false);
  const renderOptions = (start, end) => (
    <ul className="gtm-map-options">
      {gtmSlices.slice(start, end).map((slice, offset) => {
        const index = start + offset;
        return (
          <li key={slice.name}>
            <button
              type="button"
              className="gtm-map-option"
              aria-pressed={active === index}
              aria-controls="gtm-discipline-detail"
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
            >
              <span className="gtm-map-marker" aria-hidden="true" />
              <span>
                <span className="gtm-map-name font-display">{slice.name}</span>
                <span className="gtm-map-alternative">{slice.option}</span>
              </span>
              <ArrowUpRight size={19} aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="gtm-map" data-testid="gtm-pie" data-separated={separated}>
      <div className="gtm-map-composition">
        <div className="gtm-map-left">{renderOptions(0, 4)}</div>
        <div className="gtm-map-centre">
          <ScrollAccent className="gtm-map-wheel">
            <svg viewBox="-40 -40 320 320" aria-hidden="true">
              <circle className="gtm-map-orbit" cx="120" cy="120" r="149" pathLength="1" fill="none" stroke="rgba(232,228,217,0.3)" strokeWidth="0.5" />
              {Array.from({ length: 48 }, (_, index) => (
                <rect key={`tick-${index}`} x="119.5" y="-24" width="1" height={index % 6 === 0 ? "7" : "3"} fill="#E8E4D9" opacity={index % 6 === 0 ? ".65" : ".25"} transform={`rotate(${index * 7.5} 120 120)`} />
              ))}
              {gtmSlices.map((slice, index) => {
                const start = -Math.PI / 2 + index * Math.PI / 4 + 0.025;
                const centreAngle = -Math.PI / 2 + (index + .5) * Math.PI / 4;
                return <g
                  key={slice.name}
                  className="gtm-map-piece"
                  data-active={active === index}
                  data-tone={index % 2}
                  style={{
                    "--part-x": `${Math.cos(centreAngle) * 23}px`,
                    "--part-y": `${Math.sin(centreAngle) * 23}px`,
                    "--lift-x": `${Math.cos(centreAngle) * 5}px`,
                    "--lift-y": `${Math.sin(centreAngle) * 5}px`,
                    "--slice-x": `${Math.cos(centreAngle) * 35}px`,
                    "--slice-y": `${Math.sin(centreAngle) * 35}px`,
                    "--slice-delay": `${index * 85}ms`,
                  }}
                >
                  <line x1="120" y1="120" x2={120 + Math.cos(centreAngle) * 83} y2={120 + Math.sin(centreAngle) * 83} className="gtm-map-spoke" />
                  <path
                    d={donutPath(start, start + Math.PI / 4 - 0.05)}
                    className={active === index ? "is-active" : ""}
                    data-cursor
                    onMouseEnter={() => setActive(index)}
                    onClick={() => setActive(index)}
                  />
                </g>;
              })}
              <circle cx="120" cy="120" r="78" fill="none" stroke="rgba(232,228,217,0.2)" strokeWidth="0.5" />
              <g className="gtm-map-needle" style={{ transform: `rotate(${(active + .5) * 45}deg)` }}>
                <circle cx="120" cy="-29" r="4" fill="#FF4400" />
                <line x1="120" y1="-20" x2="120" y2="-10" stroke="#FF4400" strokeWidth="2" />
              </g>
            </svg>
            <button
              type="button"
              className="gtm-map-core"
              aria-label={separated ? "Connect the disciplines" : "Separate the disciplines"}
              aria-pressed={separated}
              onClick={() => setSeparated(value => !value)}
            >
              <img src="/logo-white.png" alt="GTM Consultancy" />
            </button>
          </ScrollAccent>
          <p className="gtm-map-caption">{separated ? "Click the logo to bring it together." : "Click the logo to pull it apart."}</p>
        </div>
        <div className="gtm-map-right">{renderOptions(4, 8)}</div>
      </div>
      <div id="gtm-discipline-detail" className="gtm-map-detail" data-discipline={`0${active + 1}`} aria-live="polite" aria-atomic="true">
        <p className="font-display">{gtmSlices[active].name}</p>
        <p>{gtmSlices[active].detail}</p>
      </div>
    </div>
  );
};

const ContextMessage = () => {
  const ref = useRef(null);
  const entered = useInView(ref, { once: true, amount: .35 });
  const reducedMotion = useReducedMotion();
  const interacted = useRef(false);
  const [activeSignal, setActiveSignal] = useState(0);

  useEffect(() => {
    if (!entered || reducedMotion) return;
    const timers = [1, 2].map((index) => setTimeout(() => {
      if (!interacted.current) setActiveSignal(index);
    }, index * 1200));
    return () => timers.forEach(clearTimeout);
  }, [entered, reducedMotion]);

  const selectSignal = (index) => {
    interacted.current = true;
    setActiveSignal(index);
  };

  return (
    <div ref={ref} className="message-specific bg-ink" data-context={activeSignal}>
      <div className="message-specific-heading">
        <p className="story-eyebrow"><Check size={16} aria-hidden="true" /> An example of specific outreach</p>
      </div>
      <ul className="message-signals" aria-label="Buyer signals in the message">
        {buyerSignals.map((signal, index) => (
          <li key={signal.label}>
            <button type="button" aria-pressed={activeSignal === index} aria-controls={`message-phrase-${index}`} onMouseEnter={() => selectSignal(index)} onFocus={() => selectSignal(index)} onClick={() => selectSignal(index)}>
              <span className="message-signal-label">{signal.label}<ArrowUpRight size={18} aria-hidden="true" /></span>
              <span className="message-signal-value">{signal.value}</span>
            </button>
          </li>
        ))}
      </ul>
      <blockquote className="font-display">
        Sarah, saw the <mark id="message-phrase-0" data-selected={activeSignal === 0}>Europe launch</mark>, the <mark id="message-phrase-1" data-selected={activeSignal === 1}>new data platform</mark> and <mark id="message-phrase-2" data-selected={activeSignal === 2}>two AEs starting Monday</mark>. I&apos;ve attached 30 accounts already hiring data leaders, with the decision-maker and first message for each. worth 10 secs to hear what we do?
      </blockquote>
    </div>
  );
};

export const WholeEngine = () => (
  <section id="whole-engine" className="gtm-story bg-paper text-ink" data-testid="whole-engine-section">
    <div className="story-shell story-sales">
      <div className="story-heading-pair">
        <div>
          <p className="story-eyebrow">Outreach that gets replies</p>
          <h2 className="story-title font-display">Give buyers a reason <span className="font-editorial italic text-cobalt">to reply.</span></h2>
        </div>
      </div>

      <div className="message-workbench">
        <div className="message-generic">
          <p className="story-eyebrow"><Minus size={16} aria-hidden="true" /> Could be sent to anyone</p>
          <blockquote>Hi Sarah, I wanted to hop on a quick 15 minutes to share how we help companies like yours scale revenue.</blockquote>
          <p className="message-note">No specific buyer.<br />No timely reason to act.</p>
        </div>
        <ContextMessage />
      </div>
    </div>

    <BuyerJourney />

    <div className="story-integration bg-ink text-paper">
      <div className="story-shell">
        <div className="story-heading-pair">
          <div>
            <p className="story-eyebrow">The GTM pie</p>
            <h2 className="story-title font-display">Your marketing and sales, <span className="font-editorial italic text-flame">run as one team.</span></h2>
          </div>
          <p className="story-intro">We connect content, outreach and closing in your CRM. Every buyer has an owner and a next step.</p>
        </div>
        <GtmPie />
        <section className="scale-observation" aria-labelledby="scale-observation-title">
          <div className="scale-observation-intro">
            <h3 id="scale-observation-title" className="font-display">What disconnected sales <span>costs you.</span></h3>
          </div>
          <ul className="scale-consequences">
            {stitchProblems.map((problem) => (
              <li key={problem.title}>
                <h4 className="font-display">{problem.title}</h4>
                <p>{problem.body}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  </section>
);
